import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close, Warning, Delete, CheckCircle, Info, Error } from '@mui/icons-material';

/**
 * Reusable Confirmation Dialog Component
 * Used for delete confirmations and other destructive actions
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onConfirm - Callback when confirmed
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message/content
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} type - Dialog type ('warning', 'danger', 'success', 'info')
 * @param {boolean} loading - Whether confirm action is loading
 * @param {React.ReactNode} icon - Custom icon
 */

const typeConfig = {
  warning: {
    icon: Warning,
    color: '#f59e0b',
    bgColor: '#fef3c7',
    confirmColor: '#f59e0b',
  },
  danger: {
    icon: Delete,
    color: '#dc3545',
    bgColor: '#fee2e2',
    confirmColor: '#dc3545',
  },
  success: {
    icon: CheckCircle,
    color: '#10b981',
    bgColor: '#d1fae5',
    confirmColor: '#10b981',
  },
  info: {
    icon: Info,
    color: '#3b82f6',
    bgColor: '#dbeafe',
    confirmColor: '#3b82f6',
  },
  error: {
    icon: Error,
    color: '#ef4444',
    bgColor: '#fee2e2',
    confirmColor: '#ef4444',
  },
};

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  loading = false,
  icon,
  children,
  maxWidth = 'xs',
}) => {
  const config = typeConfig[type] || typeConfig.warning;
  const IconComponent = icon || config.icon;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: config.bgColor,
            }}
          >
            <IconComponent sx={{ color: config.color }} />
          </Box>
          <Typography variant="h6" component="span" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#999',
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {children || (
          <DialogContentText sx={{ color: 'text.primary', fontSize: '14px' }}>
            {message}
          </DialogContentText>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            color: '#666',
            px: 2,
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
          sx={{
            textTransform: 'none',
            backgroundColor: config.confirmColor,
            px: 2,
            '&:hover': {
              backgroundColor: config.confirmColor,
              opacity: 0.9,
            },
          }}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Delete confirmation dialog preset
 */
export const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  itemName = 'this item',
  loading,
}) => (
  <ConfirmDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    title="Delete Confirmation"
    message={`Are you sure you want to delete ${itemName}? This action cannot be undone.`}
    confirmText="Delete"
    cancelText="Cancel"
    type="danger"
    loading={loading}
  />
);

export default ConfirmDialog;
